/**
 * Boutique TEXTIPRINT — Back-office admin CommandesBoutique (Sprint 3).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §B2 + brief extension §3 (5 canaux).
 *
 * Une seule page — liste + KPIs + filtres + Dialog création manuelle + Drawer détail
 * (Résumé / Lignes / Paiements / Livraison) + actions rapides workflow.
 */
import { useMemo, useState, useEffect } from 'react';
import {
  RefreshCw, AlertTriangle, ShoppingCart, PackageCheck, Clock, TruckIcon,
  CheckCircle2, XCircle, Plus, Search, X, Trash2, ChevronRight, DollarSign, Edit,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useCommandesBoutique, useCommandeBoutique,
  useCreateCommandeBoutique, useDeleteCommandeBoutique, useChangerStatutCommandeBoutique,
  useCreateLigneCommandeBoutique, useUpdateLigneCommandeBoutique, useDeleteLigneCommandeBoutique,
  useCreatePaiementBoutique, useUpdatePaiementBoutique,
  useUpsertLivraisonBoutique, useUpdateCommandeBoutique,
} from '@/hooks/useCommandesBoutique';
import { useProduitsBoutique } from '@/hooks/useBoutique';
import {
  CANAL_COMMANDE_BOUTIQUE_LABELS,
  STATUT_COMMANDE_BOUTIQUE_LABELS,
  STATUT_PAIEMENT_BOUTIQUE_LABELS,
  METHODE_PAIEMENT_BOUTIQUE_LABELS,
  MODE_LIVRAISON_BOUTIQUE_LABELS,
  type CanalCommandeBoutique,
  type CommandeBoutique,
  type LigneCommandeBoutique,
  type MethodePaiementBoutique,
  type ModeLivraisonBoutique,
  type PaiementBoutique,
  type StatutCommandeBoutique,
  type StatutPaiementBoutique,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import type { CreateLigneCommandeBoutiqueInlinePayload } from '@/api/textprint-commandes-boutique';
import { demander } from '@/components/ui/Fenetres';

// ── Helpers UI ────────────────────────────────────────────────────────────

const STATUT_BADGE: Record<StatutCommandeBoutique, string> = {
  recue:            'ds-bd ds-bd-info',
  paiement_verifie: 'ds-bd ds-bd-info',
  en_preparation:   'ds-bd ds-bd-warn',
  en_production:    'ds-bd ds-bd-warn',
  bat_a_valider:    'ds-bd ds-bd-warn',
  bat_valide:       'ds-bd ds-bd-p',
  expediee:         'ds-bd ds-bd-p',
  livree:           'ds-bd ds-bd-s',
  terminee:         'ds-bd ds-bd-g',
  annulee:          'ds-bd ds-bd-d',
};

const STATUT_PAIEMENT_BADGE: Record<StatutPaiementBoutique, string> = {
  en_attente: 'ds-bd ds-bd-warn',
  valide:     'ds-bd ds-bd-s',
  echoue:     'ds-bd ds-bd-d',
  rembourse:  'ds-bd ds-bd-g',
};

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const toNum = (v: string | null | undefined) => (v == null ? 0 : Number(BigInt(v)));

// Prochaine action rapide selon le statut courant (mapping simplifié).
const nextQuickAction = (statut: StatutCommandeBoutique): { statut: StatutCommandeBoutique; label: string } | null => {
  switch (statut) {
    case 'recue':            return { statut: 'paiement_verifie', label: 'Valider paiement' };
    case 'paiement_verifie': return { statut: 'en_preparation',   label: 'Démarrer préparation' };
    case 'bat_a_valider':    return { statut: 'bat_valide',       label: 'BAT validé' };
    case 'bat_valide':       return { statut: 'en_production',    label: 'Démarrer production' };
    case 'en_preparation':   return { statut: 'expediee',         label: 'Marquer expédié' };
    case 'en_production':    return { statut: 'expediee',         label: 'Marquer expédié' };
    case 'expediee':         return { statut: 'livree',           label: 'Marquer livré' };
    case 'livree':           return { statut: 'terminee',         label: 'Terminer' };
    default:                 return null;
  }
};

// ══════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════

export const CommandesBoutiquePage = () => {
  const filiale = useAuthStore((s) => s.filiale);

  const [statutFilter, setStatutFilter] = useState<StatutCommandeBoutique | ''>('');
  const [canalFilter, setCanalFilter]   = useState<CanalCommandeBoutique | ''>('');
  const [search, setSearch]             = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [error, setError]               = useState<string | null>(null);
  const [info, setInfo]                 = useState<string | null>(null);

  const [createOpen, setCreateOpen]     = useState(false);
  const [selectedId, setSelectedId]     = useState<string | null>(null);

  const queryParams = useMemo(() => ({
    limit: 500,
    statut:   statutFilter || undefined,
    canal:    canalFilter  || undefined,
    search:   search.trim() || undefined,
    dateFrom: dateFrom || undefined,
    dateTo:   dateTo   || undefined,
  }), [statutFilter, canalFilter, search, dateFrom, dateTo]);

  const { data, isLoading, refetch } = useCommandesBoutique(queryParams);
  const commandes = data?.data ?? [];

  const changerStatut = useChangerStatutCommandeBoutique();

  // KPIs (calculés depuis le set filtré, sauf CA mois qui prend tout ce qui est retourné).
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const totalRecues = commandes.filter((c) => c.statut === 'recue').length;
  const totalEnCours = commandes.filter((c) =>
    ['en_preparation', 'en_production', 'bat_a_valider', 'bat_valide'].includes(c.statut),
  ).length;
  const totalLivrees = commandes.filter((c) => c.statut === 'livree' || c.statut === 'terminee').length;
  const caMoisCents = commandes.reduce((s, c) => {
    const d = new Date(c.dateCommande);
    return d >= startOfMonth && c.statut !== 'annulee' ? s + toNum(c.totalCents) : s;
  }, 0);

  const wrapErr = async (fn: () => Promise<unknown>, successMsg?: string) => {
    setError(null); setInfo(null);
    try {
      await fn();
      if (successMsg) setInfo(successMsg);
    } catch (err) { setError(extractError(err).message); }
  };

  const onQuickAction = (c: CommandeBoutique) => {
    const next = nextQuickAction(c.statut);
    if (!next) return;
    void wrapErr(
      () => changerStatut.mutateAsync({ id: c.id, statut: next.statut }),
      `Commande ${c.reference} → ${STATUT_COMMANDE_BOUTIQUE_LABELS[next.statut]}`,
    );
  };

  return (
    <>
      <Topbar
        title="Commandes Boutique"
        subtitle={filiale ? `${commandes.length} commande(s) · ${filiale.fullName}` : 'Back-office commandes boutique'}
        actions={
          <>
            <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setCreateOpen(true)}>
              <Plus size={14} /> Nouvelle commande manuelle
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}
        {info && (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><CheckCircle2 size={18} /></span>
            <div className="ds-alert-txt">{info}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico"><ShoppingCart size={18} /></div>
            <div className="ds-kr-val">{commandes.length}</div>
            <div className="ds-kr-lbl">Total commandes</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico"><Clock size={18} /></div>
            <div className="ds-kr-val">{totalRecues}</div>
            <div className="ds-kr-lbl">Reçues (attente paiement)</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico"><PackageCheck size={18} /></div>
            <div className="ds-kr-val">{totalEnCours}</div>
            <div className="ds-kr-lbl">En cours (prépa / BAT / prod)</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico"><TruckIcon size={18} /></div>
            <div className="ds-kr-val">{totalLivrees}</div>
            <div className="ds-kr-lbl">Livrées / terminées</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico"><DollarSign size={18} /></div>
            <div className="ds-kr-val">{formatNumber(caMoisCents / 100)}</div>
            <div className="ds-kr-lbl">CA du mois (FCFA)</div>
          </div>
        </div>

        <div className="ds-filters" style={{ marginBottom: 12 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-3)' }} />
            <input
              type="text"
              placeholder="Référence / email / nom…"
              className="ds-fi"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260, paddingLeft: 28 }}
            />
          </div>
          <select
            className="ds-fi"
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value as StatutCommandeBoutique | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_COMMANDE_BOUTIQUE_LABELS) as StatutCommandeBoutique[]).map((s) => (
              <option key={s} value={s}>{STATUT_COMMANDE_BOUTIQUE_LABELS[s]}</option>
            ))}
          </select>
          <select
            className="ds-fi"
            value={canalFilter}
            onChange={(e) => setCanalFilter(e.target.value as CanalCommandeBoutique | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous canaux</option>
            {(Object.keys(CANAL_COMMANDE_BOUTIQUE_LABELS) as CanalCommandeBoutique[]).map((c) => (
              <option key={c} value={c}>{CANAL_COMMANDE_BOUTIQUE_LABELS[c]}</option>
            ))}
          </select>
          <input
            type="date"
            className="ds-fi"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ width: 'auto' }}
            title="Depuis"
          />
          <input
            type="date"
            className="ds-fi"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ width: 'auto' }}
            title="Jusqu'à"
          />
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Commandes boutique</div>
              <div className="ds-table-sub">
                {commandes.length} commande{commandes.length > 1 ? 's' : ''} — cliquer sur une ligne pour ouvrir le détail
              </div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Référence</th>
                <th style={{ width: 100 }}>Date</th>
                <th>Client</th>
                <th style={{ width: 110 }}>Canal</th>
                <th style={{ width: 150 }}>Statut</th>
                <th style={{ width: 130, textAlign: 'right' }}>Total TTC</th>
                <th style={{ width: 220 }}>Actions rapides</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              ) : commandes.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>
                  Aucune commande. Créez-en une manuellement ou attendez une commande vitrine / WhatsApp.
                </td></tr>
              ) : commandes.map((c) => {
                const quick = nextQuickAction(c.statut);
                return (
                  <tr
                    key={c.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedId(c.id)}
                  >
                    <td><code style={{ fontSize: 11, color: 'var(--tx-2)' }}>{c.reference}</code></td>
                    <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{fmtDate(c.dateCommande)}</td>
                    <td style={{ fontSize: 13 }}>
                      <div style={{ fontWeight: 500 }}>{c.nomCompletCommande}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{c.emailCommande}</div>
                    </td>
                    <td>
                      <span className="ds-bd ds-bd-g">{CANAL_COMMANDE_BOUTIQUE_LABELS[c.canal]}</span>
                    </td>
                    <td>
                      <span className={STATUT_BADGE[c.statut]}>{STATUT_COMMANDE_BOUTIQUE_LABELS[c.statut]}</span>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                      {formatNumber(toNum(c.totalCents) / 100)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {quick && (
                        <button
                          className="ds-btn ds-btn-sm ds-btn-p"
                          disabled={changerStatut.isPending}
                          onClick={() => onQuickAction(c)}
                          style={{ marginRight: 4 }}
                        >
                          {quick.label}
                        </button>
                      )}
                      <button
                        className="ds-btn ds-btn-sm ds-btn-g"
                        onClick={() => setSelectedId(c.id)}
                        title="Voir détail"
                      >
                        <ChevronRight size={11} /> Détail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && (
        <CreateCommandeDialog onClose={() => setCreateOpen(false)} onCreated={(id) => {
          setCreateOpen(false);
          setSelectedId(id);
        }} />
      )}

      {selectedId && (
        <CommandeDrawer commandeId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// DIALOG · CRÉATION MANUELLE
// ══════════════════════════════════════════════════════════════════════════

interface DraftLigne extends CreateLigneCommandeBoutiqueInlinePayload {
  _key: string;
  _produitLabel?: string;
  _prixSnapshot?: number; // en cents pour affichage
}

const CreateCommandeDialog = ({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) => {
  const create = useCreateCommandeBoutique();
  const { data: produitsData } = useProduitsBoutique({ limit: 500, actif: true });
  const produits = produitsData?.data ?? [];

  const [emailCommande, setEmailCommande]         = useState('');
  const [phoneCommande, setPhoneCommande]         = useState('');
  const [nomCompletCommande, setNomComplet]       = useState('');
  const [raisonSociale, setRaisonSociale]         = useState('');
  const [canal, setCanal]                         = useState<CanalCommandeBoutique>('whatsapp');
  const [fraisLivraisonCents, setFraisLivraison]  = useState(0);
  const [notesClient, setNotesClient]             = useState('');
  const [notesInternes, setNotesInternes]         = useState('');
  const [lignes, setLignes]                       = useState<DraftLigne[]>([]);
  const [err, setErr]                             = useState<string | null>(null);
  const [produitSearch, setProduitSearch]         = useState('');

  const filteredProduits = useMemo(() => {
    const q = produitSearch.trim().toLowerCase();
    if (!q) return produits.slice(0, 20);
    return produits
      .filter((p) => p.nom.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
      .slice(0, 20);
  }, [produits, produitSearch]);

  const addLigne = (produitId: string) => {
    const p = produits.find((x) => x.id === produitId);
    if (!p) return;
    setLignes((prev) => [...prev, {
      _key: `${Date.now()}-${Math.random()}`,
      _produitLabel: `${p.sku} · ${p.nom}`,
      _prixSnapshot: Number(BigInt(p.prixUnitaireHtCents ?? '0')),
      produitId,
      quantite:          1,
      prixUnitaireCents: Number(BigInt(p.prixUnitaireHtCents ?? '0')),
      remisePct:         0,
    }]);
    setProduitSearch('');
  };

  const updateLigne = (key: string, patch: Partial<DraftLigne>) => {
    setLignes((prev) => prev.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  };

  const removeLigne = (key: string) => setLignes((prev) => prev.filter((l) => l._key !== key));

  // Totaux estimés côté front (le backend recalcule)
  const sousTotal = lignes.reduce((s, l) => {
    const remiseFactor = (100 - (l.remisePct ?? 0)) / 100;
    return s + l.quantite * (l.prixUnitaireCents ?? 0) * remiseFactor;
  }, 0);
  const tva = Math.round(sousTotal * 0.18);
  const total = Math.round(sousTotal + tva + fraisLivraisonCents);

  const submit = async () => {
    setErr(null);
    if (!emailCommande.trim() || !nomCompletCommande.trim()) {
      setErr('Email et nom du client requis.');
      return;
    }
    if (lignes.length === 0) {
      setErr('Ajoutez au moins une ligne de commande.');
      return;
    }
    try {
      const created = await create.mutateAsync({
        emailCommande:         emailCommande.trim(),
        phoneCommande:         phoneCommande.trim() || undefined,
        nomCompletCommande:    nomCompletCommande.trim(),
        raisonSocialeCommande: raisonSociale.trim() || undefined,
        canal,
        fraisLivraisonCents:   fraisLivraisonCents || undefined,
        notesClient:           notesClient.trim() || undefined,
        notesInternes:         notesInternes.trim() || undefined,
        lignes: lignes.map((l) => ({
          produitId:         l.produitId,
          varianteId:        l.varianteId,
          designation:       l.designation,
          sku:               l.sku,
          quantite:          l.quantite,
          prixUnitaireCents: l.prixUnitaireCents,
          remisePct:         l.remisePct,
        })),
      });
      onCreated(created.id);
    } catch (e) { setErr(extractError(e).message); }
  };

  // Escape close
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div className="ds-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ds-modal size-lg">
        <header className="ds-modal-header">
          <div>
            <div className="ds-modal-title">Nouvelle commande manuelle</div>
            <div className="ds-modal-sub">Saisie WhatsApp / téléphone / vitrine physique</div>
          </div>
          <button type="button" className="ds-modal-close" onClick={onClose} aria-label="Fermer">
            <X size={16} />
          </button>
        </header>

        <div className="ds-modal-body">
          {err && (
            <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
              <span className="ds-alert-ico"><AlertTriangle size={16} /></span>
              <div className="ds-alert-txt">{err}</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Email client *
              <input type="email" className="ds-fi" value={emailCommande}
                onChange={(e) => setEmailCommande(e.target.value)} required />
            </label>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Téléphone
              <input type="tel" className="ds-fi" value={phoneCommande}
                onChange={(e) => setPhoneCommande(e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Nom complet *
              <input type="text" className="ds-fi" value={nomCompletCommande}
                onChange={(e) => setNomComplet(e.target.value)} required />
            </label>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Raison sociale (B2B)
              <input type="text" className="ds-fi" value={raisonSociale}
                onChange={(e) => setRaisonSociale(e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Canal
              <select className="ds-fi" value={canal}
                onChange={(e) => setCanal(e.target.value as CanalCommandeBoutique)}>
                {(Object.keys(CANAL_COMMANDE_BOUTIQUE_LABELS) as CanalCommandeBoutique[]).map((c) => (
                  <option key={c} value={c}>{CANAL_COMMANDE_BOUTIQUE_LABELS[c]}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Frais livraison (FCFA)
              <input type="number" min={0} className="ds-fi"
                value={fraisLivraisonCents ? fraisLivraisonCents / 100 : 0}
                onChange={(e) => setFraisLivraison(Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)))} />
            </label>
          </div>

          <div style={{ borderTop: '1px solid var(--bd-1)', paddingTop: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 8 }}>Lignes de commande</div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="text" className="ds-fi" placeholder="Rechercher produit (SKU / nom)…"
                value={produitSearch} onChange={(e) => setProduitSearch(e.target.value)}
                style={{ flex: 1 }} />
            </div>
            {produitSearch && (
              <div style={{
                border: '1px solid var(--bd-1)', borderRadius: 6, maxHeight: 180, overflowY: 'auto',
                marginBottom: 8, background: 'var(--bg-1)',
              }}>
                {filteredProduits.length === 0 ? (
                  <div style={{ padding: 8, fontSize: 12, color: 'var(--tx-3)' }}>Aucun produit trouvé.</div>
                ) : filteredProduits.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => addLigne(p.id)}
                    style={{ padding: 6, borderBottom: '1px solid var(--bd-1)', cursor: 'pointer', fontSize: 12 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <code style={{ fontSize: 10, color: 'var(--tx-3)' }}>{p.sku}</code> · {p.nom} ·{' '}
                    <span style={{ color: 'var(--tx-3)' }}>
                      {formatMontant(p.prixUnitaireHtCents ?? '0')} FCFA HT
                    </span>
                  </div>
                ))}
              </div>
            )}

            {lignes.length === 0 ? (
              <div style={{ padding: 12, fontSize: 12, color: 'var(--tx-3)', textAlign: 'center',
                border: '1px dashed var(--bd-1)', borderRadius: 6 }}>
                Aucune ligne. Recherchez un produit ci-dessus pour l'ajouter.
              </div>
            ) : (
              <table className="ds-tbl" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th style={{ width: 70 }}>Qté</th>
                    <th style={{ width: 120 }}>PU HT (FCFA)</th>
                    <th style={{ width: 80 }}>Remise %</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Total HT</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => {
                    const remiseFactor = (100 - (l.remisePct ?? 0)) / 100;
                    const totalLigne = l.quantite * (l.prixUnitaireCents ?? 0) * remiseFactor;
                    return (
                      <tr key={l._key}>
                        <td>{l._produitLabel}</td>
                        <td>
                          <input type="number" min={1} className="ds-fi" value={l.quantite}
                            onChange={(e) => updateLigne(l._key, { quantite: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                            style={{ width: '100%', padding: 4 }} />
                        </td>
                        <td>
                          <input type="number" min={0} className="ds-fi"
                            value={(l.prixUnitaireCents ?? 0) / 100}
                            onChange={(e) => updateLigne(l._key, { prixUnitaireCents: Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)) })}
                            style={{ width: '100%', padding: 4 }} />
                        </td>
                        <td>
                          <input type="number" min={0} max={100} step={0.5} className="ds-fi"
                            value={l.remisePct ?? 0}
                            onChange={(e) => updateLigne(l._key, { remisePct: Math.min(100, Math.max(0, parseFloat(e.target.value || '0'))) })}
                            style={{ width: '100%', padding: 4 }} />
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {formatNumber(totalLigne / 100)}
                        </td>
                        <td>
                          <button className="ds-btn ds-btn-sm ds-btn-d" onClick={() => removeLigne(l._key)} title="Retirer">
                            <Trash2 size={11} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Notes client
              <textarea className="ds-fi" rows={2} value={notesClient}
                onChange={(e) => setNotesClient(e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--tx-2)' }}>
              Notes internes
              <textarea className="ds-fi" rows={2} value={notesInternes}
                onChange={(e) => setNotesInternes(e.target.value)} />
            </label>
          </div>

          <div style={{ borderTop: '1px solid var(--bd-1)', paddingTop: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--tx-3)' }}>
              <span>Sous-total HT</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(sousTotal / 100)} FCFA</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--tx-3)' }}>
              <span>TVA 18%</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(tva / 100)} FCFA</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--tx-3)' }}>
              <span>Frais livraison</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(fraisLivraisonCents / 100)} FCFA</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4 }}>
              <span>Total TTC</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(total / 100)} FCFA</span>
            </div>
          </div>
        </div>

        <div className="ds-modal-footer">
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={() => void submit()} disabled={create.isPending}>
            {create.isPending ? 'Création…' : 'Créer la commande'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// DRAWER · DÉTAIL COMMANDE (Résumé / Lignes / Paiements / Livraison)
// ══════════════════════════════════════════════════════════════════════════

type DrawerTab = 'resume' | 'lignes' | 'paiements' | 'livraison';

const CommandeDrawer = ({ commandeId, onClose }: { commandeId: string; onClose: () => void }) => {
  const { data: commande, isLoading } = useCommandeBoutique(commandeId);
  const [tab, setTab] = useState<DrawerTab>('resume');
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const changerStatut = useChangerStatutCommandeBoutique();
  const removeCommande = useDeleteCommandeBoutique();

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  const wrap = async (fn: () => Promise<unknown>, ok?: string) => {
    setErr(null); setInfo(null);
    try { await fn(); if (ok) setInfo(ok); } catch (e) { setErr(extractError(e).message); }
  };

  const doQuick = () => {
    if (!commande) return;
    const next = nextQuickAction(commande.statut);
    if (!next) return;
    void wrap(
      () => changerStatut.mutateAsync({ id: commande.id, statut: next.statut }),
      `Statut → ${STATUT_COMMANDE_BOUTIQUE_LABELS[next.statut]}`,
    );
  };

  const doAnnuler = async () => {
    if (!commande) return;
    if (!(await demander.confirmer({ titre: `Annuler la commande ${commande.reference} ?`, confirmer: 'Annuler la commande', danger: true }))) return;
    void wrap(
      () => changerStatut.mutateAsync({ id: commande.id, statut: 'annulee' }),
      `Commande annulée`,
    );
  };

  const doDelete = async () => {
    if (!commande) return;
    if (!(await demander.confirmer({ titre: `Supprimer définitivement la commande ${commande.reference} ?`, confirmer: 'Supprimer', danger: true }))) return;
    void wrap(async () => {
      await removeCommande.mutateAsync(commande.id);
      onClose();
    });
  };

  return (
    <div className="ds-drawer-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="ds-drawer">
        {isLoading || !commande ? (
          <div style={{ padding: 32, color: 'var(--tx-3)', fontSize: 13 }}>Chargement…</div>
        ) : (
          <>
            <header className="ds-drawer-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ds-dh-title">{commande.nomCompletCommande}</div>
                <div className="ds-dh-ref">{commande.reference}</div>
                <div className="ds-dh-badges">
                  <span className="ds-dh-badge">{CANAL_COMMANDE_BOUTIQUE_LABELS[commande.canal]}</span>
                  <span className="ds-dh-badge">{STATUT_COMMANDE_BOUTIQUE_LABELS[commande.statut]}</span>
                </div>
              </div>
              <button type="button" className="ds-drawer-close" onClick={onClose} aria-label="Fermer">
                <X size={18} />
              </button>
            </header>

            <div className="ds-drawer-body">
              {err && (
                <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
                  <span className="ds-alert-ico"><AlertTriangle size={16} /></span>
                  <div className="ds-alert-txt">{err}</div>
                </div>
              )}
              {info && (
                <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 12 }}>
                  <span className="ds-alert-ico"><CheckCircle2 size={16} /></span>
                  <div className="ds-alert-txt">{info}</div>
                </div>
              )}

              <div className="ds-tabs-bar" style={{ marginBottom: 12 }}>
                {(['resume', 'lignes', 'paiements', 'livraison'] as DrawerTab[]).map((t) => (
                  <button
                    key={t}
                    className={`ds-tab ${tab === t ? 'is-active' : ''}`}
                    onClick={() => setTab(t)}
                  >
                    {t === 'resume' ? 'Résumé' : t === 'lignes' ? 'Lignes' : t === 'paiements' ? 'Paiements' : 'Livraison'}
                  </button>
                ))}
              </div>

              {tab === 'resume'    && <TabResume    commande={commande} />}
              {tab === 'lignes'    && <TabLignes    commande={commande} onErr={setErr} onInfo={setInfo} />}
              {tab === 'paiements' && <TabPaiements commande={commande} onErr={setErr} onInfo={setInfo} />}
              {tab === 'livraison' && <TabLivraison commande={commande} onErr={setErr} onInfo={setInfo} />}
            </div>

            <div className="ds-drawer-actions">
              {nextQuickAction(commande.statut) && (
                <button className="ds-btn ds-btn-p" onClick={doQuick} disabled={changerStatut.isPending}>
                  {nextQuickAction(commande.statut)!.label}
                </button>
              )}
              {['recue', 'paiement_verifie', 'bat_a_valider'].includes(commande.statut) && (
                <button className="ds-btn ds-btn-warn" onClick={doAnnuler}>
                  <XCircle size={12} /> Annuler
                </button>
              )}
              {commande.statut === 'recue' && (
                <button className="ds-btn ds-btn-d" onClick={doDelete}>
                  <Trash2 size={12} /> Supprimer
                </button>
              )}
              <button className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

// ── Onglet Résumé ────────────────────────────────────────────────────────

const TabResume = ({ commande }: { commande: CommandeBoutique }) => {
  const sous = toNum(commande.sousTotalHtCents);
  const rC   = toNum(commande.remiseClientCents);
  const rP   = toNum(commande.remiseCodeCents);
  const frais = toNum(commande.fraisLivraisonCents);
  const tva   = toNum(commande.tvaCents);
  const total = toNum(commande.totalCents);

  return (
    <>
      <div className="ds-drawer-section">
        <div className="ds-drawer-section-title">Client</div>
        <div style={{ fontSize: 13 }}>
          <div><strong>{commande.nomCompletCommande}</strong>{commande.raisonSocialeCommande ? ` · ${commande.raisonSocialeCommande}` : ''}</div>
          <div style={{ color: 'var(--tx-3)' }}>{commande.emailCommande}{commande.phoneCommande ? ` · ${commande.phoneCommande}` : ''}</div>
        </div>
      </div>

      <div className="ds-drawer-section">
        <div className="ds-drawer-section-title">Montants (FCFA)</div>
        <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 4 }}>
          <span style={{ color: 'var(--tx-3)' }}>Sous-total HT</span>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatNumber(sous / 100)}</span>
          <span style={{ color: 'var(--tx-3)' }}>Remise client</span>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>−{formatNumber(rC / 100)}</span>
          <span style={{ color: 'var(--tx-3)' }}>Remise code promo</span>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>−{formatNumber(rP / 100)}</span>
          <span style={{ color: 'var(--tx-3)' }}>TVA 18%</span>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatNumber(tva / 100)}</span>
          <span style={{ color: 'var(--tx-3)' }}>Frais livraison</span>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatNumber(frais / 100)}</span>
          <span style={{ fontWeight: 700 }}>Total TTC</span>
          <span style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(total / 100)}</span>
        </div>
      </div>

      <div className="ds-drawer-section">
        <div className="ds-drawer-section-title">Timeline</div>
        <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
          <div>Commande passée : {fmtDateTime(commande.dateCommande)}</div>
          <div>Livraison prévue : {fmtDate(commande.dateLivraisonPrevue)}</div>
          <div>Livraison réelle : {fmtDateTime(commande.dateLivraisonReelle)}</div>
          <div>Dernière MAJ : {fmtDateTime(commande.updatedAt)}</div>
        </div>
      </div>

      {(commande.notesClient || commande.notesInternes) && (
        <div className="ds-drawer-section">
          <div className="ds-drawer-section-title">Notes</div>
          {commande.notesClient && <div style={{ fontSize: 12, marginBottom: 6 }}><strong>Client :</strong> {commande.notesClient}</div>}
          {commande.notesInternes && <div style={{ fontSize: 12, color: 'var(--tx-3)' }}><strong>Interne :</strong> {commande.notesInternes}</div>}
        </div>
      )}
    </>
  );
};

// ── Onglet Lignes ────────────────────────────────────────────────────────

const TabLignes = ({
  commande, onErr, onInfo,
}: { commande: CommandeBoutique; onErr: (m: string) => void; onInfo: (m: string) => void }) => {
  const lignes = commande.lignes ?? [];
  const { data: produitsData } = useProduitsBoutique({ limit: 500, actif: true });
  const produits = produitsData?.data ?? [];

  const createLigne = useCreateLigneCommandeBoutique();
  const updateLigne = useUpdateLigneCommandeBoutique();
  const deleteLigne = useDeleteLigneCommandeBoutique();

  const [addOpen, setAddOpen]         = useState(false);
  const [produitSearch, setProduitSearch] = useState('');
  const [addProduitId, setAddProduitId]   = useState('');
  const [addQte, setAddQte]           = useState(1);
  const [addPu, setAddPu]             = useState<number | ''>('');
  const [addRemise, setAddRemise]     = useState(0);
  const [editKey, setEditKey]         = useState<string | null>(null);
  const [editQte, setEditQte]         = useState(1);
  const [editPu, setEditPu]           = useState(0);
  const [editRemise, setEditRemise]   = useState(0);

  const filtered = useMemo(() => {
    const q = produitSearch.trim().toLowerCase();
    if (!q) return produits.slice(0, 20);
    return produits.filter((p) => p.sku.toLowerCase().includes(q) || p.nom.toLowerCase().includes(q)).slice(0, 20);
  }, [produits, produitSearch]);

  const doAdd = async () => {
    if (!addProduitId) { onErr('Sélectionne un produit'); return; }
    try {
      await createLigne.mutateAsync({
        commandeId: commande.id,
        payload: {
          produitId:         addProduitId,
          quantite:          addQte,
          prixUnitaireCents: addPu === '' ? undefined : addPu,
          remisePct:         addRemise || undefined,
        },
      });
      onInfo('Ligne ajoutée');
      setAddOpen(false); setAddProduitId(''); setAddQte(1); setAddPu(''); setAddRemise(0); setProduitSearch('');
    } catch (e) { onErr(extractError(e).message); }
  };

  const startEdit = (l: LigneCommandeBoutique) => {
    setEditKey(l.id);
    setEditQte(l.quantite);
    setEditPu(toNum(l.prixUnitaireCents));
    setEditRemise(parseFloat(l.remisePct));
  };

  const doEdit = async (l: LigneCommandeBoutique) => {
    try {
      await updateLigne.mutateAsync({
        commandeId: commande.id, lineId: l.id,
        payload: { quantite: editQte, prixUnitaireCents: editPu, remisePct: editRemise },
      });
      onInfo('Ligne modifiée');
      setEditKey(null);
    } catch (e) { onErr(extractError(e).message); }
  };

  const doDelete = async (l: LigneCommandeBoutique) => {
    if (!(await demander.confirmer({ titre: `Supprimer la ligne "${l.designation}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    try {
      await deleteLigne.mutateAsync({ commandeId: commande.id, lineId: l.id });
      onInfo('Ligne supprimée');
    } catch (e) { onErr(extractError(e).message); }
  };

  return (
    <div className="ds-drawer-section">
      <div className="ds-drawer-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Lignes ({lignes.length})</span>
        <button className="ds-btn ds-btn-sm ds-btn-p" onClick={() => setAddOpen((v) => !v)}>
          <Plus size={11} /> {addOpen ? 'Fermer' : 'Ajouter'}
        </button>
      </div>

      {addOpen && (
        <div style={{ padding: 8, border: '1px solid var(--bd-1)', borderRadius: 6, marginBottom: 8 }}>
          <input type="text" className="ds-fi" placeholder="Rechercher produit…"
            value={produitSearch} onChange={(e) => setProduitSearch(e.target.value)}
            style={{ marginBottom: 6, width: '100%' }} />
          {produitSearch && (
            <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid var(--bd-1)', borderRadius: 4, marginBottom: 6 }}>
              {filtered.map((p) => (
                <div key={p.id}
                  onClick={() => { setAddProduitId(p.id); setAddPu(Number(BigInt(p.prixUnitaireHtCents ?? '0'))); setProduitSearch(`${p.sku} · ${p.nom}`); }}
                  style={{ padding: 4, fontSize: 11, cursor: 'pointer', borderBottom: '1px solid var(--bd-1)' }}
                >
                  <code>{p.sku}</code> · {p.nom}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 6 }}>
            <label style={{ fontSize: 11 }}>Qté
              <input type="number" min={1} className="ds-fi" value={addQte}
                onChange={(e) => setAddQte(Math.max(1, parseInt(e.target.value || '1', 10)))} />
            </label>
            <label style={{ fontSize: 11 }}>PU (FCFA)
              <input type="number" min={0} className="ds-fi"
                value={addPu === '' ? '' : addPu / 100}
                onChange={(e) => {
                  const v = e.target.value;
                  setAddPu(v === '' ? '' : Math.max(0, Math.round(parseFloat(v) * 100)));
                }} />
            </label>
            <label style={{ fontSize: 11 }}>Remise %
              <input type="number" min={0} max={100} className="ds-fi" value={addRemise}
                onChange={(e) => setAddRemise(Math.min(100, Math.max(0, parseFloat(e.target.value || '0'))))} />
            </label>
          </div>
          <button className="ds-btn ds-btn-sm ds-btn-p" onClick={() => void doAdd()} disabled={createLigne.isPending}>
            {createLigne.isPending ? 'Ajout…' : 'Ajouter la ligne'}
          </button>
        </div>
      )}

      {lignes.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--tx-3)', padding: 12, textAlign: 'center' }}>
          Aucune ligne pour cette commande.
        </div>
      ) : (
        <table className="ds-tbl" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Produit</th>
              <th style={{ width: 50 }}>Qté</th>
              <th style={{ width: 100 }}>PU HT</th>
              <th style={{ width: 60 }}>Remise</th>
              <th style={{ width: 100, textAlign: 'right' }}>Total HT</th>
              <th style={{ width: 70 }}></th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => {
              const editing = editKey === l.id;
              return (
                <tr key={l.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{l.designation}</div>
                    <code style={{ fontSize: 10, color: 'var(--tx-3)' }}>{l.sku}</code>
                  </td>
                  <td>
                    {editing ? (
                      <input type="number" min={1} className="ds-fi" value={editQte}
                        onChange={(e) => setEditQte(Math.max(1, parseInt(e.target.value || '1', 10)))}
                        style={{ width: '100%', padding: 2 }} />
                    ) : l.quantite}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {editing ? (
                      <input type="number" min={0} className="ds-fi" value={editPu / 100}
                        onChange={(e) => setEditPu(Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)))}
                        style={{ width: '100%', padding: 2 }} />
                    ) : formatNumber(toNum(l.prixUnitaireCents) / 100)}
                  </td>
                  <td>
                    {editing ? (
                      <input type="number" min={0} max={100} className="ds-fi" value={editRemise}
                        onChange={(e) => setEditRemise(Math.min(100, Math.max(0, parseFloat(e.target.value || '0'))))}
                        style={{ width: '100%', padding: 2 }} />
                    ) : `${parseFloat(l.remisePct)}%`}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                    {formatNumber(toNum(l.montantHtCents) / 100)}
                  </td>
                  <td>
                    {editing ? (
                      <>
                        <button className="ds-btn ds-btn-sm ds-btn-s" onClick={() => void doEdit(l)}
                          disabled={updateLigne.isPending} title="Enregistrer">
                          <CheckCircle2 size={11} />
                        </button>
                        <button className="ds-btn ds-btn-sm ds-btn-g" onClick={() => setEditKey(null)} title="Annuler">
                          <X size={11} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="ds-btn ds-btn-sm ds-btn-g" onClick={() => startEdit(l)} title="Modifier">
                          <Edit size={11} />
                        </button>
                        <button className="ds-btn ds-btn-sm ds-btn-d" onClick={() => void doDelete(l)}
                          disabled={deleteLigne.isPending} title="Supprimer">
                          <Trash2 size={11} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Onglet Paiements ─────────────────────────────────────────────────────

const TabPaiements = ({
  commande, onErr, onInfo,
}: { commande: CommandeBoutique; onErr: (m: string) => void; onInfo: (m: string) => void }) => {
  const paiements = commande.paiements ?? [];
  const createPaiement = useCreatePaiementBoutique();
  const updatePaiement = useUpdatePaiementBoutique();

  const [open, setOpen]           = useState(false);
  const [methode, setMethode]     = useState<MethodePaiementBoutique>('wave');
  const [montant, setMontant]     = useState(0);
  const [statut, setStatut]       = useState<StatutPaiementBoutique>('en_attente');
  const [reference, setReference] = useState('');
  const [notes, setNotes]         = useState('');

  const doAdd = async () => {
    if (montant <= 0) { onErr('Montant requis'); return; }
    try {
      await createPaiement.mutateAsync({
        commandeId: commande.id,
        payload: {
          methode, statut,
          montantCents: montant,
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      });
      onInfo('Paiement enregistré');
      setOpen(false); setMontant(0); setReference(''); setNotes(''); setStatut('en_attente');
    } catch (e) { onErr(extractError(e).message); }
  };

  const doValider = async (p: PaiementBoutique) => {
    try {
      await updatePaiement.mutateAsync({
        commandeId: commande.id, paiementId: p.id,
        payload: { statut: 'valide' },
      });
      onInfo(`Paiement ${p.reference ?? p.id.slice(0, 8)} validé`);
    } catch (e) { onErr(extractError(e).message); }
  };

  const doRefuser = async (p: PaiementBoutique) => {
    try {
      await updatePaiement.mutateAsync({
        commandeId: commande.id, paiementId: p.id,
        payload: { statut: 'echoue' },
      });
      onInfo(`Paiement marqué échoué`);
    } catch (e) { onErr(extractError(e).message); }
  };

  const totalPaye = paiements
    .filter((p) => p.statut === 'valide')
    .reduce((s, p) => s + toNum(p.montantCents), 0);
  const totalTtc = toNum(commande.totalCents);
  const reste = totalTtc - totalPaye;

  return (
    <div className="ds-drawer-section">
      <div className="ds-drawer-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Paiements ({paiements.length})</span>
        <button className="ds-btn ds-btn-sm ds-btn-p" onClick={() => setOpen((v) => !v)}>
          <Plus size={11} /> {open ? 'Fermer' : 'Enregistrer paiement'}
        </button>
      </div>

      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: 'var(--bg-2)', borderRadius: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--tx-3)' }}>Total TTC</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(totalTtc / 100)} FCFA</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--tx-s, #10b981)' }}>
          <span>Encaissé</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(totalPaye / 100)} FCFA</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
          <span>Reste dû</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(reste / 100)} FCFA</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: 8, border: '1px solid var(--bd-1)', borderRadius: 6, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
            <label style={{ fontSize: 11 }}>Méthode
              <select className="ds-fi" value={methode}
                onChange={(e) => setMethode(e.target.value as MethodePaiementBoutique)}>
                {(Object.keys(METHODE_PAIEMENT_BOUTIQUE_LABELS) as MethodePaiementBoutique[]).map((m) => (
                  <option key={m} value={m}>{METHODE_PAIEMENT_BOUTIQUE_LABELS[m]}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 11 }}>Statut
              <select className="ds-fi" value={statut}
                onChange={(e) => setStatut(e.target.value as StatutPaiementBoutique)}>
                <option value="en_attente">En attente</option>
                <option value="valide">Validé</option>
              </select>
            </label>
            <label style={{ fontSize: 11 }}>Montant (FCFA)
              <input type="number" min={1} className="ds-fi" value={montant ? montant / 100 : ''}
                onChange={(e) => setMontant(Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)))} />
            </label>
            <label style={{ fontSize: 11 }}>Référence (Wave / OM / virement…)
              <input type="text" className="ds-fi" value={reference}
                onChange={(e) => setReference(e.target.value)} />
            </label>
          </div>
          <label style={{ fontSize: 11 }}>Notes
            <textarea className="ds-fi" rows={1} value={notes}
              onChange={(e) => setNotes(e.target.value)} />
          </label>
          <button className="ds-btn ds-btn-sm ds-btn-p" onClick={() => void doAdd()} disabled={createPaiement.isPending}
            style={{ marginTop: 6 }}>
            {createPaiement.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}

      {paiements.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--tx-3)', padding: 12, textAlign: 'center' }}>
          Aucun paiement enregistré.
        </div>
      ) : (
        <table className="ds-tbl" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Méthode</th>
              <th style={{ textAlign: 'right' }}>Montant</th>
              <th>Réf</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paiements.map((p) => (
              <tr key={p.id}>
                <td>{fmtDate(p.createdAt)}</td>
                <td>{METHODE_PAIEMENT_BOUTIQUE_LABELS[p.methode]}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(toNum(p.montantCents) / 100)}
                </td>
                <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{p.reference ?? '—'}</td>
                <td><span className={STATUT_PAIEMENT_BADGE[p.statut]}>{STATUT_PAIEMENT_BOUTIQUE_LABELS[p.statut]}</span></td>
                <td>
                  {p.statut === 'en_attente' && (
                    <>
                      <button className="ds-btn ds-btn-sm ds-btn-s" onClick={() => void doValider(p)}
                        disabled={updatePaiement.isPending} title="Valider">
                        <CheckCircle2 size={11} />
                      </button>
                      <button className="ds-btn ds-btn-sm ds-btn-d" onClick={() => void doRefuser(p)}
                        disabled={updatePaiement.isPending} title="Refuser">
                        <XCircle size={11} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Onglet Livraison ─────────────────────────────────────────────────────

const TabLivraison = ({
  commande, onErr, onInfo,
}: { commande: CommandeBoutique; onErr: (m: string) => void; onInfo: (m: string) => void }) => {
  const livraison = commande.livraison;
  const upsert = useUpsertLivraisonBoutique();
  const changerStatut = useChangerStatutCommandeBoutique();
  const updateCommande = useUpdateCommandeBoutique();

  const [mode, setMode]                 = useState<ModeLivraisonBoutique>(livraison?.mode ?? 'livraison_abidjan');
  const [adresseLigne1, setLigne1]      = useState(livraison?.adresseLigne1 ?? '');
  const [adresseLigne2, setLigne2]      = useState(livraison?.adresseLigne2 ?? '');
  const [ville, setVille]               = useState(livraison?.ville ?? '');
  const [zone, setZone]                 = useState(livraison?.zone ?? '');
  const [livreurNom, setLivreurNom]     = useState(livraison?.livreurNom ?? '');
  const [livreurPhone, setLivreurPhone] = useState(livraison?.livreurPhone ?? '');
  const [trackingCode, setTracking]     = useState(livraison?.trackingCode ?? '');
  const [fraisFcfa, setFraisFcfa]       = useState<number>(toNum(commande.fraisLivraisonCents) / 100);

  useEffect(() => {
    if (!livraison) return;
    setMode(livraison.mode);
    setLigne1(livraison.adresseLigne1);
    setLigne2(livraison.adresseLigne2 ?? '');
    setVille(livraison.ville);
    setZone(livraison.zone ?? '');
    setLivreurNom(livraison.livreurNom ?? '');
    setLivreurPhone(livraison.livreurPhone ?? '');
    setTracking(livraison.trackingCode ?? '');
  }, [livraison]);

  const doSave = async () => {
    if (!adresseLigne1.trim() || !ville.trim()) { onErr('Adresse ligne 1 et ville requises'); return; }
    try {
      await upsert.mutateAsync({
        commandeId: commande.id,
        payload: {
          mode, adresseLigne1: adresseLigne1.trim(), adresseLigne2: adresseLigne2.trim() || undefined,
          ville: ville.trim(), zone: zone.trim() || undefined,
          livreurNom: livreurNom.trim() || undefined, livreurPhone: livreurPhone.trim() || undefined,
          trackingCode: trackingCode.trim() || undefined,
        },
      });
      // Sync frais livraison au niveau commande si modifié.
      const currentFrais = toNum(commande.fraisLivraisonCents) / 100;
      if (fraisFcfa !== currentFrais) {
        await updateCommande.mutateAsync({
          id: commande.id,
          payload: { fraisLivraisonCents: Math.round(fraisFcfa * 100) },
        });
      }
      onInfo('Livraison enregistrée');
    } catch (e) { onErr(extractError(e).message); }
  };

  const doMarquerExpediee = async () => {
    try {
      await changerStatut.mutateAsync({ id: commande.id, statut: 'expediee' });
      onInfo('Commande marquée expédiée');
    } catch (e) { onErr(extractError(e).message); }
  };

  const doMarquerLivree = async () => {
    try {
      await changerStatut.mutateAsync({ id: commande.id, statut: 'livree' });
      onInfo('Commande marquée livrée');
    } catch (e) { onErr(extractError(e).message); }
  };

  return (
    <div className="ds-drawer-section">
      <div className="ds-drawer-section-title">Livraison</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label style={{ fontSize: 11, gridColumn: '1 / -1' }}>Mode
          <select className="ds-fi" value={mode} onChange={(e) => setMode(e.target.value as ModeLivraisonBoutique)}>
            {(Object.keys(MODE_LIVRAISON_BOUTIQUE_LABELS) as ModeLivraisonBoutique[]).map((m) => (
              <option key={m} value={m}>{MODE_LIVRAISON_BOUTIQUE_LABELS[m]}</option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11, gridColumn: '1 / -1' }}>Adresse ligne 1 *
          <input type="text" className="ds-fi" value={adresseLigne1} onChange={(e) => setLigne1(e.target.value)} />
        </label>
        <label style={{ fontSize: 11, gridColumn: '1 / -1' }}>Adresse ligne 2
          <input type="text" className="ds-fi" value={adresseLigne2} onChange={(e) => setLigne2(e.target.value)} />
        </label>
        <label style={{ fontSize: 11 }}>Ville *
          <input type="text" className="ds-fi" value={ville} onChange={(e) => setVille(e.target.value)} />
        </label>
        <label style={{ fontSize: 11 }}>Zone (Cocody, Yopougon…)
          <input type="text" className="ds-fi" value={zone} onChange={(e) => setZone(e.target.value)} />
        </label>
        <label style={{ fontSize: 11 }}>Livreur — nom
          <input type="text" className="ds-fi" value={livreurNom} onChange={(e) => setLivreurNom(e.target.value)} />
        </label>
        <label style={{ fontSize: 11 }}>Livreur — téléphone
          <input type="text" className="ds-fi" value={livreurPhone} onChange={(e) => setLivreurPhone(e.target.value)} />
        </label>
        <label style={{ fontSize: 11 }}>Code tracking
          <input type="text" className="ds-fi" value={trackingCode} onChange={(e) => setTracking(e.target.value)} />
        </label>
        <label style={{ fontSize: 11 }}>Frais livraison (FCFA)
          <input type="number" min={0} className="ds-fi" value={fraisFcfa}
            onChange={(e) => setFraisFcfa(Math.max(0, parseFloat(e.target.value || '0')))} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        <button className="ds-btn ds-btn-sm ds-btn-p" onClick={() => void doSave()}
          disabled={upsert.isPending || updateCommande.isPending}>
          {upsert.isPending ? 'Enregistrement…' : livraison ? 'Mettre à jour' : 'Enregistrer'}
        </button>
        {['en_preparation', 'en_production'].includes(commande.statut) && (
          <button className="ds-btn ds-btn-sm ds-btn-warn" onClick={() => void doMarquerExpediee()}>
            <TruckIcon size={11} /> Marquer expédié
          </button>
        )}
        {commande.statut === 'expediee' && (
          <button className="ds-btn ds-btn-sm ds-btn-s" onClick={() => void doMarquerLivree()}>
            <CheckCircle2 size={11} /> Marquer livré
          </button>
        )}
      </div>

      {livraison && (
        <div style={{ marginTop: 12, padding: 8, background: 'var(--bg-2)', borderRadius: 6, fontSize: 11, color: 'var(--tx-3)' }}>
          <div>Expédiée le : {fmtDateTime(livraison.dateExpedition)}</div>
          <div>Livrée le : {fmtDateTime(livraison.dateLivraison)}</div>
          {livraison.trackingCode && <div>Tracking : <code>{livraison.trackingCode}</code></div>}
        </div>
      )}
    </div>
  );
};
